/** DOM-building functions. No framework -- plain element creation, one
 * function per piece of UI, matching the brand's minimal-JS console. */
import type {
  ClientRow,
  DeadlinesPayload,
  DetailPayload,
  FindingItem,
  NeedsYou,
  NextDeadline,
  SeverityStyle,
  XeroConnectionRow,
} from "./api.js";
import {
  AGENTS,
  COPY,
  ONBOARDING,
  buildUrl,
  filterButtonLabel,
  matchesSearch,
  selectionSummary,
  toggleClient,
  viewKey,
} from "./nav.js";
import type { AgentInfo, FilterOption, View } from "./nav.js";

// Every figure that represents an inferred TIME GAP (not a plain count,
// not a calendar due date) carries this label wherever it's shown --
// display-only, the underlying engine value is unchanged.
const GAP_CAPTION = "Inferred from ledger activity";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// Manual ISO-string splitting, not `new Date(iso)` -- parsing a
// date-only ISO string as UTC and then formatting in the viewer's local
// timezone can shift the displayed day by one; splitting the string
// avoids that entirely for a value that's a date only, never a instant.
function splitIsoDate(iso: string): { year: number; month: number; day: number } {
  const parts = iso.split("-").map(Number);
  return { year: parts[0]!, month: parts[1]!, day: parts[2]! };
}

/** "21 October" -- matches the deadline line's prose format. */
function formatDayMonth(iso: string): string {
  const { month, day } = splitIsoDate(iso);
  return `${day} ${MONTH_NAMES[month - 1]}`;
}

/** "2:05 pm" in the viewer's own time zone -- for an instant, not a date. */
function formatClockTime(iso: string): string {
  return new Date(iso).toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit" });
}

/** "21/10/2026" -- PRODUCT-UI.md's table date convention. */
function formatTableDate(iso: string): string {
  const { year, month, day } = splitIsoDate(iso);
  return `${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}/${year}`;
}

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string
): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (className) e.className = className;
  if (text !== undefined) e.textContent = text;
  return e;
}

function severityPill(label: string, style: SeverityStyle): HTMLElement {
  return el("span", `status pk-severity-${style}`, label);
}

// ---- Rail navigation ----

export interface RailOptions {
  active: View;
  clients: readonly string[]; // kept in every link, so the filter follows the person
  followUpCount: number;
  showOnboarding: boolean; // account_admin and super_admin only
  showMeetings?: boolean; // where Meetings is on (cdk.json meetingsEnabled), for every member
  showFeedback: boolean; // any signed-in member (decision 0031)
  feedbackOpen: boolean;
  onNavigate: (view: View) => void;
  onFeedback: () => void;
  // Hosted console only (decision 0025): a signed-in session.
  showSignOut?: boolean;
  onSignOut?: () => void;
  // The signed-in account's email (Cognito's, via /api/me), shown above
  // Sign out so a person can see whose session this is. Display only.
  signedInEmail?: string | null;
}

/** Neutral grey, never coral: coral stays on the Follow-up count alone. */
function soonPill(text: string = COPY.soonPill): HTMLElement {
  return el("span", "pk-soon-pill", text);
}

/** One rail item: a real link (deep link, opens in a new tab if asked),
 * handled in place on a plain click. */
function railLink(view: View, label: string, opts: RailOptions, extra?: HTMLElement): HTMLAnchorElement {
  const link = el("a", "pk-rail-link");
  link.href = buildUrl(view, opts.clients);
  if (viewKey(view) === viewKey(opts.active)) link.setAttribute("aria-current", "page");
  link.append(el("span", undefined, label));
  if (extra) link.append(extra);
  link.addEventListener("click", (ev) => {
    if (ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.button !== 0) return;
    ev.preventDefault();
    opts.onNavigate(view);
  });
  return link;
}

export function renderRail(container: HTMLElement, opts: RailOptions): void {
  container.replaceChildren();

  // Coral appears here only, and only while there is something waiting
  // (PRODUCT-UI.md s.2: "the one place coral may appear").
  const badge =
    opts.followUpCount > 0 ? el("span", "pk-count-badge figure", String(opts.followUpCount)) : undefined;
  if (badge) badge.setAttribute("aria-label", `${opts.followUpCount} to follow up`);
  container.append(railLink({ kind: "follow-up" }, "Follow-up", opts, badge));
  container.append(railLink({ kind: "deadlines" }, "Deadlines", opts));
  if (opts.showMeetings) {
    // A client's own page keeps Clients highlighted.
    const railOpts = opts.active.kind === "client" ? { ...opts, active: { kind: "clients" } as View } : opts;
    container.append(railLink({ kind: "clients" }, "Clients", railOpts));
    container.append(railLink({ kind: "meetings" }, "Meetings", opts));
  }

  const groupLabel = el("p", "pk-rail-group", "Agents");
  groupLabel.id = "pk-rail-agents";
  const group = el("div", "pk-rail-group-items");
  group.setAttribute("role", "group");
  group.setAttribute("aria-labelledby", groupLabel.id);
  for (const agent of AGENTS) group.append(railLink({ kind: "agent", agent }, agent.name, opts, soonPill()));
  container.append(groupLabel, group);

  container.append(el("hr", "pk-rail-divider"));
  if (opts.showOnboarding) container.append(railLink({ kind: "onboarding" }, ONBOARDING.name, opts, soonPill()));
  container.append(railLink({ kind: "settings" }, "Settings", opts));
}

/** Feedback sits at the foot of the rail, on every screen -- the rail
 * convention, not a floating widget (docs/closed-beta-spec.md s.7). It
 * opens a panel beside the current view rather than replacing it, so
 * the person can still see what they're writing about. */
export function renderRailFooter(container: HTMLElement, opts: RailOptions): void {
  container.replaceChildren();
  if (opts.showFeedback) {
    const button = el("button", "pk-rail-link", undefined);
    button.type = "button";
    button.setAttribute("aria-expanded", String(opts.feedbackOpen));
    button.setAttribute("aria-controls", "pk-detail");
    if (opts.feedbackOpen) button.setAttribute("aria-current", "true");
    button.append(el("span", undefined, "Send feedback"));
    button.addEventListener("click", () => opts.onFeedback());
    container.append(button);
  }
  if (opts.showSignOut && opts.signedInEmail) {
    const who = el("p", "pk-rail-account");
    // A line break is allowed straight after "@", so a long address
    // wraps as "name@" / "domain" rather than mid-word.
    const email = el("span", "pk-rail-account-email");
    const at = opts.signedInEmail.indexOf("@");
    if (at > 0) {
      email.append(opts.signedInEmail.slice(0, at + 1), document.createElement("wbr"), opts.signedInEmail.slice(at + 1));
    } else {
      email.textContent = opts.signedInEmail;
    }
    who.append(el("span", "pk-rail-account-label", "Signed in as"), email);
    container.append(who);
  }
  if (opts.showSignOut && opts.onSignOut) {
    const onSignOut = opts.onSignOut;
    const signOut = el("button", "pk-rail-link pk-rail-signout", undefined);
    signOut.type = "button";
    signOut.append(el("span", undefined, "Sign out"));
    signOut.addEventListener("click", () => onSignOut());
    container.append(signOut);
  }
}

// ---- Follow-up ----

/** "Next step: ..." -- the one suggested action for a finding type
 * (views.NEXT_STEPS), labelled so it reads as a suggestion, not a fact. */
function nextStep(text: string): HTMLElement {
  const p = el("p", "pk-next-step");
  p.append(el("span", "pk-next-step-label", "Next step: "), document.createTextNode(text));
  return p;
}

function findingCard(item: FindingItem, selected: boolean, onSelect: () => void): HTMLElement {
  const card = el("div", "pk-card");
  card.setAttribute("role", "button");
  card.tabIndex = 0;
  if (selected) card.setAttribute("aria-current", "true");

  const top = el("div", "pk-card-top");
  top.append(el("span", "pk-card-title", item.finding_type));
  top.append(severityPill(item.severity_label, item.severity_style));
  card.append(top);

  card.append(el("div", "pk-card-sub", `${item.client_name} · ${item.bank_account_name}`));
  card.append(el("div", "pk-card-reasoning", item.reasoning));
  card.append(nextStep(item.next_step));
  // The deadline isn't repeated as its own line here -- the reasoning
  // sentence above already states it ("next deadline in N days").

  const activate = () => onSelect();
  card.addEventListener("click", activate);
  card.addEventListener("keydown", (ev) => {
    if (ev.key === "Enter" || ev.key === " ") {
      ev.preventDefault();
      activate();
    }
  });
  return card;
}

function findingGroup(
  title: string,
  items: FindingItem[],
  emptyText: string,
  selectedKey: string | null,
  onSelect: (item: FindingItem) => void
): HTMLElement {
  const group = el("div", "pk-group");
  group.append(el("div", "pk-group-title", title));
  if (items.length === 0) {
    group.append(el("div", "pk-empty", emptyText));
    return group;
  }
  for (const item of items) {
    const key = `${item.client_slug}/${item.bank_account_id}`;
    group.append(findingCard(item, key === selectedKey, () => onSelect(item)));
  }
  return group;
}

export function renderFollowUp(
  container: HTMLElement,
  needsYou: NeedsYou,
  selectedKey: string | null,
  onSelect: (item: FindingItem) => void
): void {
  container.replaceChildren();
  container.append(
    findingGroup("At risk", needsYou.at_risk, "Nothing at risk right now.", selectedKey, onSelect)
  );
  container.append(
    findingGroup(
      "Needs review",
      needsYou.needs_review,
      "Nothing needs review right now.",
      selectedKey,
      onSelect
    )
  );
  container.append(
    findingGroup(
      "Waiting on others",
      needsYou.waiting_on_others,
      "Nothing is waiting on someone else right now.",
      selectedKey,
      onSelect
    )
  );
}

// ---- Detail ----

function detailRow(label: string, value: string): HTMLElement {
  const row = el("div", "pk-detail-row");
  row.append(el("span", "pk-detail-label", label));
  row.append(el("span", "pk-detail-value", value));
  return row;
}

function detailCaption(text: string): HTMLElement {
  return el("div", "pk-detail-caption muted", text);
}

export function renderDetail(container: HTMLElement, detail: DetailPayload | null): void {
  container.replaceChildren();

  if (detail === null) {
    container.append(el("div", "pk-empty", "Select a finding to see its detail."));
    return;
  }

  container.append(el("h3", undefined, detail.bank_account_name));
  container.append(el("div", "pk-card-sub", detail.client_name));

  if (detail.finding) {
    const f = detail.finding;
    const findingBlock = el("div");
    findingBlock.style.margin = "var(--pk-space-4) 0";
    const top = el("div", "pk-card-top");
    top.append(el("span", "pk-card-title", f.finding_type));
    top.append(severityPill(f.severity_label, f.severity_style));
    findingBlock.append(top);
    findingBlock.append(el("p", "muted", f.reasoning));
    findingBlock.append(nextStep(f.next_step));
    container.append(findingBlock);
  } else {
    container.append(el("p", "muted", "No active finding for this account."));
  }

  const s = detail.signals;
  container.append(
    detailRow(
      "Days since last transaction",
      s.days_since_last_transaction === null ? "—" : String(s.days_since_last_transaction)
    )
  );
  container.append(detailCaption(GAP_CAPTION));
  if (s.gap_baseline) {
    container.append(detailRow("Usual gap (median)", `${s.gap_baseline.median_days.toFixed(1)} days`));
    container.append(detailCaption(GAP_CAPTION));
    container.append(detailRow("Gap threshold", `${s.gap_baseline.threshold_days.toFixed(1)} days`));
    container.append(detailCaption(GAP_CAPTION));
  }
  container.append(
    detailRow(
      "Recurring payees missing",
      `${s.missing_recurring_payee_count} of ${s.recurring_payee_count}`
    )
  );
  if (s.volume) {
    container.append(detailRow("Transactions this period", String(s.volume.count_this_period)));
    container.append(detailRow("Trailing average", s.volume.trailing_average.toFixed(1)));
    container.append(detailRow("Same period last year", s.volume.same_period_last_year.toFixed(1)));
    container.append(
      detailRow("Historically regular volume", s.volume.is_historically_regular ? "Yes" : "No")
    );
  }
  container.append(detailRow("Unreconciled transactions", String(s.unreconciled_count)));
  // Names which deadline and its actual date -- "September IAS, 21
  // October" -- not just a day count, which repeats what the reasoning
  // sentence above already says without being any more specific.
  if (detail.next_deadline) {
    const nd: NextDeadline = detail.next_deadline;
    container.append(detailRow("Next deadline", `${nd.label}, ${formatDayMonth(nd.due_date)}`));
  }
}

// ---- Settings: Xero connections (decision 0030) ----

export interface ConnectionsViewState {
  rows: XeroConnectionRow[] | null; // null while loading
  loadError: string | null;
  notice: string | null; // what Preekie just did, after a disconnect
  selectedSlug: string | null;
}

export function renderConnections(
  container: HTMLElement,
  view: ConnectionsViewState,
  onDisconnect: (row: XeroConnectionRow) => void,
  onReload: () => void,
  onReconnect: (row: XeroConnectionRow) => void = () => undefined
): void {
  container.replaceChildren();

  const section = el("section", "pk-group");
  const titleRow = el("div", "pk-card-top");
  const title = el("h2", "pk-section-title", "Xero connections");
  title.id = "pk-xero-connections-title";
  titleRow.append(title);
  const reload = el("button", "btn btn-outline", "Check now");
  reload.type = "button";
  reload.disabled = view.rows === null && view.loadError === null;
  reload.addEventListener("click", onReload);
  titleRow.append(reload);
  section.append(titleRow);
  section.append(
    el(
      "p",
      "muted pk-section-intro",
      "The Xero organisations Preekie reads for this account. Preekie checks them with Xero at most once a minute. " +
        "Select Check now to ask Xero again straight away."
    )
  );

  if (view.notice) {
    const notice = el("div", "pk-notice", view.notice);
    notice.setAttribute("role", "status");
    section.append(notice);
  }

  if (view.loadError) {
    const alert = el("div", "pk-alert", view.loadError);
    alert.setAttribute("role", "alert");
    section.append(alert);
    container.append(section);
    return;
  }
  if (view.rows === null) {
    section.append(el("div", "pk-empty", "Checking your connections with Xero…"));
    container.append(section);
    return;
  }
  if (view.rows.length === 0) {
    section.append(el("div", "pk-empty", "No Xero organisations are connected to this account yet."));
    container.append(section);
    return;
  }

  // The oldest check on screen is the honest answer to "how current is
  // this?" -- rows can come from different checks.
  const oldest = view.rows.map((r) => r.checked_at).sort()[0];
  if (oldest) section.append(el("p", "muted pk-checked-at", `Checked with Xero at ${formatClockTime(oldest)}.`));

  const table = el("table", "pk-table pk-table-static");
  table.setAttribute("aria-labelledby", title.id);
  const headRow = el("tr");
  headRow.append(
    el("th", undefined, "Client"),
    el("th", undefined, "Xero organisation"),
    el("th", undefined, "Connection"),
    el("th", undefined, "")
  );
  const thead = el("thead");
  thead.append(headRow);
  table.append(thead);

  const tbody = el("tbody");
  for (const row of view.rows) {
    const tr = el("tr");
    if (row.client_slug === view.selectedSlug) tr.setAttribute("aria-current", "true");
    tr.append(el("td", undefined, row.client_name));
    tr.append(el("td", undefined, row.organisation_name ?? "—"));

    const stateCell = el("td");
    const stateLine = el("div", "pk-connection-state");
    // Unreachable is the brand's Exception status (a failed check),
    // shown with its own label; "Connected" is plain text, not a status.
    if (row.status_style === "exception") stateLine.append(el("span", "status pk-severity-exception", "Exception"));
    stateLine.append(el("span", undefined, row.status_label));
    stateCell.append(stateLine);
    if (row.message) stateCell.append(el("div", "pk-card-reasoning", row.message));
    tr.append(stateCell);

    const actionCell = el("td", "pk-action-cell");
    if (row.status === "connected" && row.connection_id) {
      const button = el("button", "btn btn-destructive", "Disconnect");
      button.type = "button";
      button.setAttribute("aria-label", `Disconnect ${row.organisation_name ?? row.client_name}`);
      button.addEventListener("click", () => onDisconnect(row));
      actionCell.append(button);
    }
    if (row.can_reconnect) {
      const button = el("button", "btn btn-primary", "Reconnect");
      button.type = "button";
      button.setAttribute("aria-label", `Reconnect ${row.client_name}`);
      button.addEventListener("click", () => onReconnect(row));
      actionCell.append(button);
    }
    tr.append(actionCell);
    tbody.append(tr);
  }
  table.append(tbody);
  section.append(table);
  container.append(section);
}

export interface DisconnectPanelState {
  row: XeroConnectionRow;
  pending: boolean;
  error: string | null;
}

/** Destructive actions are always confirmed (PRODUCT-UI.md s.4). The
 * confirm button names exactly what it will disconnect. */
export function renderDisconnectPanel(
  container: HTMLElement,
  panel: DisconnectPanelState,
  onConfirm: () => void,
  onCancel: () => void
): void {
  container.replaceChildren();
  const org = panel.row.organisation_name ?? panel.row.client_name;

  const heading = el("h3", undefined, `Disconnect ${org}?`);
  heading.id = "pk-disconnect-title";
  heading.tabIndex = -1; // focus moves here when the confirmation opens
  container.append(heading);
  container.append(
    el(
      "p",
      "muted",
      `Preekie will stop reading ${panel.row.client_name}'s Xero data. Nothing inside Xero changes and nothing is deleted. ` +
        "Preekie can only read it again once the organisation is reconnected."
    )
  );
  container.append(
    el("p", "muted", "Only this organisation is disconnected. Other organisations stay connected.")
  );

  if (panel.error) {
    const alert = el("div", "pk-alert", panel.error);
    alert.setAttribute("role", "alert");
    container.append(alert);
  }

  const actions = el("div", "pk-actions");
  const confirm = el("button", "btn btn-destructive", panel.pending ? "Disconnecting…" : `Disconnect ${org}`);
  confirm.type = "button";
  confirm.disabled = panel.pending;
  confirm.addEventListener("click", onConfirm);
  const cancel = el("button", "btn btn-outline", "Cancel");
  cancel.type = "button";
  cancel.disabled = panel.pending;
  cancel.addEventListener("click", onCancel);
  actions.append(confirm, cancel);
  container.append(actions);
}

export interface ReconnectPanelState {
  row: XeroConnectionRow;
  pending: boolean;
  error: string | null;
  link: { url: string; expiresAt: string } | null;
  copied: boolean;
}

function shortTime(iso: string): string {
  const d = new Date(iso);
  const h = d.getHours() % 12 || 12;
  return `${h}:${String(d.getMinutes()).padStart(2, "0")} ${d.getHours() < 12 ? "am" : "pm"}`;
}

/** Reconnect (decision 0047): get a one-time Xero consent link, then open
 * it -- or copy it for whoever can sign in to this organisation in Xero.
 * Preekie only accepts the same organisation back. */
export function renderReconnectPanel(
  container: HTMLElement,
  panel: ReconnectPanelState,
  onGetLink: () => void,
  onCopy: () => void,
  onClose: () => void
): void {
  container.replaceChildren();
  const name = panel.row.client_name;
  const heading = el("h3", undefined, `Reconnect ${name}`);
  heading.id = "pk-reconnect-title";
  heading.tabIndex = -1;
  container.append(heading);
  container.append(
    el(
      "p",
      "muted",
      `Preekie needs fresh permission from someone who can sign in to ${name}'s organisation in Xero. ` +
        "In Xero, choose the same organisation as before; Preekie won't accept a different one."
    )
  );

  if (panel.error) {
    const alert = el("div", "pk-alert", panel.error);
    alert.setAttribute("role", "alert");
    container.append(alert);
  }

  const actions = el("div", "pk-actions");
  if (!panel.link) {
    const get = el("button", "btn btn-primary", panel.pending ? "Getting link…" : "Get reconnect link");
    get.type = "button";
    get.disabled = panel.pending;
    get.addEventListener("click", onGetLink);
    actions.append(get);
  } else {
    container.append(
      el(
        "p",
        "muted",
        `The link works once and expires at ${shortTime(panel.link.expiresAt)}. ` +
          "If someone else has access to this organisation in Xero, copy the link and send it to them."
      )
    );
    const open = el("a", "btn btn-primary", "Open Xero");
    open.href = panel.link.url;
    open.target = "_blank";
    open.rel = "noopener noreferrer";
    const copy = el("button", "btn btn-outline", panel.copied ? "Link copied" : "Copy link");
    copy.type = "button";
    copy.addEventListener("click", onCopy);
    actions.append(open, copy);
  }
  const close = el("button", "btn btn-outline", panel.link ? "Done" : "Cancel");
  close.type = "button";
  close.addEventListener("click", onClose);
  actions.append(close);
  container.append(actions);
}

// ---- Feedback (decision 0031) ----

export interface FeedbackPanelState {
  sending: boolean;
  error: string | null;
  sent: string | null; // the server's thank-you, after a successful send
}

export interface FeedbackPanel {
  root: HTMLElement;
  update(state: FeedbackPanelState): void;
  values(): { doing: string; expected: string; details: string };
  clear(): void;
  focusFirst(): void;
  /** Fills "What were you doing?" (e.g. with an agent's name) and puts
   * the cursor in the next field. */
  prefill(doing: string): void;
}

/** Built once and kept, not rebuilt on every render -- rebuilding would
 * throw away what the person is typing (and their cursor) whenever
 * anything else on the page re-rendered. */
export function createFeedbackPanel(onSend: () => void, onClose: () => void): FeedbackPanel {
  const root = el("form", "pk-feedback");
  root.setAttribute("aria-labelledby", "pk-feedback-title");
  root.noValidate = true;

  const heading = el("h3", undefined, "Send feedback");
  heading.id = "pk-feedback-title";
  root.append(heading);
  root.append(el("p", "muted", "Tell us what happened. The Preekie team reads every message."));

  function field(id: string, label: string): HTMLTextAreaElement {
    const wrap = el("div", "pk-field");
    const lab = el("label", undefined, label);
    lab.htmlFor = id;
    const area = el("textarea");
    area.id = id;
    area.rows = 3;
    area.maxLength = 2000;
    wrap.append(lab, area);
    root.append(wrap);
    return area;
  }
  const doing = field("pk-feedback-doing", "What were you doing?");
  const expected = field("pk-feedback-expected", "What did you expect to happen?");
  const details = field("pk-feedback-details", "Anything else?");

  const message = el("div");
  root.append(message);

  const actions = el("div", "pk-actions");
  const send = el("button", "btn btn-primary", "Send feedback");
  send.type = "submit";
  const close = el("button", "btn btn-outline", "Close");
  close.type = "button";
  close.addEventListener("click", onClose);
  actions.append(send, close);
  root.append(actions);

  root.addEventListener("submit", (ev) => {
    ev.preventDefault();
    onSend();
  });

  return {
    root,
    update(state) {
      send.disabled = state.sending;
      send.textContent = state.sending ? "Sending…" : "Send feedback";
      message.replaceChildren();
      if (state.error) {
        const alert = el("div", "pk-alert", state.error);
        alert.setAttribute("role", "alert");
        message.append(alert);
      } else if (state.sent) {
        const notice = el("div", "pk-notice", state.sent);
        notice.setAttribute("role", "status");
        message.append(notice);
      }
    },
    values: () => ({ doing: doing.value, expected: expected.value, details: details.value }),
    clear() {
      doing.value = "";
      expected.value = "";
      details.value = "";
    },
    focusFirst: () => doing.focus(),
    prefill(text) {
      doing.value = text;
      expected.focus();
    },
  };
}

// ---- Top bar (every view) ----

export interface TopBarOptions {
  title: string;
  followUpCount: number | null; // shown beside the title on a phone, where the rail is hidden
  menuOpen: boolean;
  onMenu: () => void;
  refresh: { lastChecked: string; refreshing: boolean; onRefresh: () => void } | null;
  filter: HTMLElement;
}

export function renderTopBar(opts: TopBarOptions): HTMLElement {
  const bar = el("header", "pk-topbar");

  const lead = el("div", "pk-topbar-lead");
  const menu = el("button", "pk-menu-button");
  menu.type = "button";
  menu.setAttribute("aria-label", opts.menuOpen ? "Close menu" : "Open menu");
  menu.setAttribute("aria-expanded", String(opts.menuOpen));
  menu.setAttribute("aria-controls", "pk-rail");
  menu.innerHTML =
    '<svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" focusable="false">' +
    '<path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" stroke-width="2" stroke-linecap="round" fill="none"/></svg>';
  menu.addEventListener("click", opts.onMenu);
  const title = el("h1", "pk-title", opts.title);
  title.id = "pk-title";
  title.tabIndex = -1;
  lead.append(menu, title);
  if (opts.followUpCount !== null && opts.followUpCount > 0) {
    const badge = el("span", "pk-count-badge figure pk-title-badge", String(opts.followUpCount));
    badge.setAttribute("aria-label", `${opts.followUpCount} to follow up`);
    lead.append(badge);
  }
  bar.append(lead);

  const actions = el("div", "pk-topbar-actions");
  if (opts.refresh) {
    const checked = el("span", "pk-last-checked", opts.refresh.lastChecked);
    checked.setAttribute("aria-live", "polite");
    const button = el("button", "btn btn-primary", opts.refresh.refreshing ? "Refreshing…" : "Refresh");
    button.type = "button";
    button.disabled = opts.refresh.refreshing;
    button.addEventListener("click", opts.refresh.onRefresh);
    actions.append(checked, button);
  }
  actions.append(opts.filter);
  bar.append(actions);
  return bar;
}

// ---- Client filter ----

export interface ClientFilter {
  root: HTMLElement;
  update(options: readonly FilterOption[], selected: readonly string[]): void;
  close(): void;
}

/** Built once and kept (like the feedback form), so a search being typed
 * survives every re-render. Choices apply straight away through
 * `onChange`; none selected means all clients. */
export function createClientFilter(onChange: (selected: string[]) => void): ClientFilter {
  let options: readonly FilterOption[] = [];
  let selected: readonly string[] = [];

  const root = el("div", "pk-filter");
  const button = el("button", "btn btn-outline pk-filter-button");
  button.type = "button";
  button.setAttribute("aria-haspopup", "true");
  button.setAttribute("aria-expanded", "false");
  button.setAttribute("aria-controls", "pk-filter-panel");
  const buttonLabel = el("span", "pk-filter-label", "Clients");
  const buttonValue = el("span", "pk-filter-value");
  const chevron = el("span", "pk-filter-chevron");
  chevron.setAttribute("aria-hidden", "true");
  chevron.textContent = "▾";
  button.append(buttonLabel, buttonValue, chevron);

  const panel = el("div", "pk-filter-panel");
  panel.id = "pk-filter-panel";
  panel.hidden = true;
  panel.setAttribute("role", "group");
  panel.setAttribute("aria-label", "Choose clients");

  const searchLabel = el("label", "pk-visually-hidden", "Search clients");
  searchLabel.htmlFor = "pk-filter-search";
  const search = el("input", "pk-filter-search");
  search.id = "pk-filter-search";
  search.type = "search";
  search.placeholder = "Search clients";
  search.autocomplete = "off";

  const allRow = el("label", "pk-filter-option pk-filter-all");
  const allBox = el("input");
  allBox.type = "checkbox";
  allRow.append(allBox, el("span", undefined, "All clients"));

  const list = el("div", "pk-filter-list");
  const footer = el("div", "pk-filter-footer");
  const summary = el("span", "pk-filter-summary");
  summary.setAttribute("aria-live", "polite");
  const clear = el("button", "pk-filter-clear", "Clear selection");
  clear.type = "button";
  footer.append(summary, clear);
  panel.append(searchLabel, search, allRow, list, footer);
  root.append(button, panel);

  function choose(next: string[]): void {
    selected = next;
    draw();
    onChange(next);
  }

  function draw(): void {
    buttonValue.textContent = filterButtonLabel(selected, options);
    button.setAttribute("aria-label", `Clients: ${filterButtonLabel(selected, options)}`);
    allBox.checked = selected.length === 0;
    summary.textContent = selectionSummary(selected, options);
    clear.disabled = selected.length === 0;
    list.replaceChildren();
    const query = search.value;
    const shown = options.filter((o) => matchesSearch(o.client_name, query));
    for (const option of shown) {
      const row = el("label", "pk-filter-option");
      const box = el("input");
      box.type = "checkbox";
      box.checked = selected.includes(option.client_slug);
      box.addEventListener("change", () => choose(toggleClient(selected, option.client_slug, options)));
      row.append(box, el("span", undefined, option.client_name));
      list.append(row);
    }
    if (shown.length === 0) list.append(el("p", "pk-empty", "No clients match that search."));
  }

  function open(): void {
    panel.hidden = false;
    button.setAttribute("aria-expanded", "true");
    search.focus();
  }
  function close(): void {
    if (panel.hidden) return;
    panel.hidden = true;
    button.setAttribute("aria-expanded", "false");
  }

  button.addEventListener("click", () => (panel.hidden ? open() : close()));
  search.addEventListener("input", draw);
  allBox.addEventListener("change", () => choose([]));
  clear.addEventListener("click", () => choose([]));
  root.addEventListener("keydown", (ev) => {
    if (ev.key === "Escape" && !panel.hidden) {
      ev.stopPropagation();
      close();
      button.focus();
    }
  });
  document.addEventListener("click", (ev) => {
    if (!root.contains(ev.target as Node)) close();
  });

  return {
    root,
    update(nextOptions, nextSelected) {
      options = nextOptions;
      selected = nextSelected;
      draw();
    },
    close,
  };
}

// ---- One-client summary strip ----

const SHORT_MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "21 Oct" -- the brand's tight-UI date. */
function formatShortDate(iso: string): string {
  const { month, day } = splitIsoDate(iso);
  return `${day} ${SHORT_MONTH_NAMES[month - 1]}`;
}

export function renderSummaryStrip(row: ClientRow): HTMLElement {
  const strip = el("section", "pk-summary");
  strip.setAttribute("aria-label", `${row.client_name} summary`);
  strip.append(el("h2", "pk-summary-name", row.client_name));

  function fact(label: string, value: Node | string): void {
    const item = el("div", "pk-summary-fact");
    item.append(el("span", "pk-summary-label", label));
    const v = el("span", "pk-summary-value");
    v.append(value);
    item.append(v);
    strip.append(item);
  }

  fact("Lodgement pattern", row.lodgement_pattern ?? "Not set yet");
  if (row.next_deadline) {
    const value = document.createDocumentFragment();
    value.append(`${row.next_deadline.label}, `, el("span", "figure", formatShortDate(row.next_deadline.due_date)));
    fact("Next deadline", value);
  } else {
    fact("Next deadline", "None set");
  }
  const xero = document.createDocumentFragment();
  if (row.xero_status_style === "exception") {
    xero.append(el("span", "status pk-severity-exception", "Exception"), ` ${row.xero_status_label}`);
  } else if (row.xero_status === "connected" && row.refreshed_at) {
    xero.append(`${row.xero_status_label} · checked ${formatClockTime(row.refreshed_at)}`);
  } else {
    xero.append(row.xero_status_label);
  }
  fact("Xero", xero);
  return strip;
}

// ---- Deadlines ----

/** One of the brand's six statuses only where one applies: the Exception
 * pill beside the text for "At risk"; plain text otherwise. */
function statusCell(label: string, style: "exception" | null): HTMLElement {
  const cell = el("td");
  const line = el("div", "pk-connection-state");
  if (style === "exception") line.append(el("span", "status pk-severity-exception", "Exception"));
  line.append(el("span", undefined, label));
  cell.append(line);
  return cell;
}

export function renderDeadlines(container: HTMLElement, payload: DeadlinesPayload): void {
  container.replaceChildren();
  if (payload.deadlines.length > 0) {
    const table = el("table", "pk-table pk-table-static pk-deadlines");
    const caption = el("caption", "pk-visually-hidden", "Upcoming lodgements, soonest first");
    const headRow = el("tr");
    headRow.append(
      el("th", undefined, "Lodgement"),
      el("th", undefined, "Client"),
      el("th", undefined, "Due"),
      el("th", "pk-num", "Days left"),
      el("th", undefined, "Status")
    );
    const thead = el("thead");
    thead.append(headRow);
    const tbody = el("tbody");
    for (const row of payload.deadlines) {
      const tr = el("tr");
      // data-label: on a phone each row stacks as a card, labelled inline.
      const cells: Array<[string, HTMLElement]> = [
        ["Lodgement", el("td", "pk-deadline-label", row.label)],
        ["Client", el("td", undefined, row.client_name)],
        ["Due", el("td", "figure", formatTableDate(row.due_date))],
        ["Days left", el("td", "pk-num figure", row.days_left === 0 ? "Today" : String(row.days_left))],
        ["Status", statusCell(row.status_label, row.status_style)],
      ];
      for (const [label, cell] of cells) {
        cell.dataset.label = label;
        tr.append(cell);
      }
      tbody.append(tr);
    }
    table.append(caption, thead, tbody);
    const wrap = el("div", "pk-table-wrap");
    wrap.append(table);
    container.append(wrap);
  } else {
    container.append(el("div", "pk-empty", "No deadlines to show for these clients."));
  }
  for (const client of payload.clients) {
    if (client.lodgement_pattern === null) {
      container.append(el("p", "muted pk-note", `No lodgement pattern is set for ${client.client_name} yet.`));
    }
  }
  container.append(el("p", "muted pk-footnote", COPY.deadlinesFootnote));
}

// ---- "Soon" pages: agents and Onboarding ----

export function renderSoonPage(container: HTMLElement, info: AgentInfo, onTellUs: () => void): void {
  container.replaceChildren();
  const card = el("section", "pk-soon");
  card.setAttribute("aria-labelledby", "pk-soon-title");
  card.append(soonPill(COPY.comingSoonPill));
  const name = el("h2", "pk-soon-title", info.name);
  name.id = "pk-soon-title";
  card.append(name, el("p", "pk-soon-description", info.description));
  const button = el("button", "btn btn-outline", COPY.tellUs);
  button.type = "button";
  button.addEventListener("click", onTellUs);
  card.append(button);
  container.append(card);
}

// ---- Settings ----

export interface SettingsOptions {
  xeroConnections: HTMLElement | null; // account admins only; null hides the section
  email: string | null;
}

function settingsCard(title: string, id: string): HTMLElement {
  const card = el("section", "pk-settings-card");
  card.setAttribute("aria-labelledby", id);
  const heading = el("h2", "pk-section-title", title);
  heading.id = id;
  const top = el("div", "pk-card-top");
  top.append(heading);
  card.append(top);
  return card;
}

export function renderSettings(container: HTMLElement, opts: SettingsOptions): void {
  container.replaceChildren();
  const grid = el("div", "pk-settings-grid");

  if (opts.xeroConnections) {
    const card = el("div", "pk-settings-card pk-settings-wide");
    card.append(opts.xeroConnections);
    grid.append(card);
  }

  for (const [title, id] of [
    ["Subscription and billing", "pk-settings-billing"],
    ["Users and access", "pk-settings-users"],
  ] as const) {
    const card = settingsCard(title, id);
    card.querySelector(".pk-card-top")!.append(soonPill());
    card.append(el("p", "muted pk-section-intro", COPY.notAvailableYet));
    grid.append(card);
  }

  const profile = settingsCard("Your profile", "pk-settings-profile");
  const row = el("div", "pk-detail-row pk-profile-row");
  row.append(el("span", "pk-detail-label", "Email"), el("span", "pk-profile-email", opts.email ?? "—"));
  profile.append(row, el("p", "muted pk-section-intro", COPY.profileHelp));
  grid.append(profile);

  container.append(grid);
}

// ---- Not found ----

export function renderNotFound(container: HTMLElement, onHome: () => void): void {
  container.replaceChildren();
  container.append(el("p", "muted", COPY.notFoundBody));
  const link = el("a", "pk-auth-link", "Go to Follow-up");
  link.href = "/follow-up";
  link.addEventListener("click", (ev) => {
    ev.preventDefault();
    onHome();
  });
  container.append(link);
}
